import { t } from "../i18n";
import type { MeasureMode, MeasureResult, MeasureTool, SavedMeasurement } from "../viewer/MeasureTool";
import { formatArea, formatMetres, formatVolume } from "../util/format";
import { clear, el, keyValue } from "./dom";

function modes(): Array<{ mode: MeasureMode; icon: string; title: string }> {
  return [
    { mode: "line", icon: "📏", title: t().measureLine },
    { mode: "area", icon: "📐", title: t().measureArea },
    { mode: "volume", icon: "📦", title: t().measureVolume },
  ];
}

/** Hooks into the app's saved-measurement store; kept separate so this stays free of I/O. */
export interface MeasureStoreHooks {
  listSaved(): SavedMeasurement[];
  /** Name of the model the saved list belongs to, or `null` when nothing is loaded yet. */
  modelLabel(): string | null;
  saveCurrent(name: string): void;
  loadSaved(id: string): void;
  renameSaved(id: string): void;
  deleteSaved(id: string): void;
  /** Called whenever the user deliberately starts a fresh shape (a mode button, or Clear) —
   *  invalidates "editing an existing saved measurement" so the next save creates a new one
   *  instead of overwriting whatever was last loaded. */
  startNew(): void;
}

export interface MeasureToolboxHandle {
  /** Re-renders the saved list, e.g. after a load finishes or a save round-trips. */
  refreshSaved(): void;
}

/**
 * Mini toolbox docked to the right edge of the viewport: three mode buttons, a result panel
 * that appears once a shape has at least one point, and a list of measurements saved to the
 * project. Kept separate from the main sidebar so it stays reachable regardless of which
 * data section is open or collapsed.
 */
export function buildMeasureToolbox(
  container: HTMLElement,
  measure: MeasureTool,
  store: MeasureStoreHooks,
): MeasureToolboxHandle {
  const rail = el("div", { class: "measure-rail" });
  const buttons = new Map<MeasureMode, HTMLButtonElement>();

  for (const { mode, icon, title } of modes()) {
    const button = el("button", { type: "button", title }, icon);
    button.addEventListener("click", () => {
      store.startNew();
      measure.setMode(measure.mode === mode ? null : mode);
      render(measure.result());
    });
    buttons.set(mode, button);
    rail.append(button);
  }

  const panel = el("div", { class: "measure-panel" });
  panel.hidden = true;
  const saved = el("div", { class: "measure-saved" });
  container.append(rail, panel, saved);

  const renderSaved = (): void => {
    clear(saved);
    const model = store.modelLabel();
    const list = store.listSaved();
    // Always shown, even with nothing saved yet — a list that only appears once it has
    // content is a list nobody can find in the first place.
    saved.append(
      el("div", { class: "measure-saved-title", text: model ? t().savedFor(model) : t().saved }),
    );
    if (list.length === 0) {
      saved.append(
        el("div", {
          class: "hint",
          text: model ? t().noSavedForModel : t().openModelToSave,
        }),
      );
    }
    for (const item of list) {
      const summary =
        item.mode === "line"
          ? formatMetres(item.length)
          : item.mode === "area"
            ? formatArea(item.area ?? 0)
            : formatVolume(item.volume ?? 0);
      const load = el("button", { class: "icon", type: "button", title: t().showMeasurement }, "↥");
      load.addEventListener("click", () => store.loadSaved(item.id));
      const rename = el("button", { class: "icon", type: "button", title: t().renameMeasurement }, "✎");
      rename.addEventListener("click", () => store.renameSaved(item.id));
      const del = el("button", { class: "icon", type: "button", title: t().deleteMeasurement }, "🗑");
      del.addEventListener("click", () => {
        if (window.confirm(t().deleteConfirm(item.name))) store.deleteSaved(item.id);
      });
      saved.append(
        el(
          "div",
          { class: "row measure-saved-item" },
          el("span", { class: "grow", text: `${item.name} · ${summary}` }),
          load,
          rename,
          del,
        ),
      );
    }
  };

  const render = (result: MeasureResult | null): void => {
    for (const [mode, button] of buttons) button.dataset.active = String(measure.mode === mode);

    if (!result) {
      panel.hidden = true;
      return;
    }
    panel.hidden = false;
    clear(panel);

    const lengthLabel = result.mode === "line" ? t().lengthLabel : result.closed ? t().perimeterLabel : t().lengthSoFarLabel;
    const rows: Array<[string, string]> = [[lengthLabel, formatMetres(result.length)]];
    if (result.area !== null) rows.push([t().areaPlan, formatArea(result.area)]);
    if (result.mode === "volume" && result.volume !== null) rows.push([t().volumeEst, formatVolume(result.volume)]);
    panel.append(keyValue(rows));

    if (result.mode === "volume" && result.base !== null) {
      const baseInput = el("input", { type: "number", step: "0.1", value: result.base.toFixed(3) });
      baseInput.addEventListener("change", () => {
        const value = Number(baseInput.value);
        if (Number.isFinite(value)) measure.setBaseReal(value);
      });
      panel.append(el("div", { class: "field" }, el("span", { text: t().baseElevation }), baseInput));
    }

    const finishNeeded = result.mode === "line" ? result.pointCount >= 2 : result.pointCount >= 3;
    const undo = el(
      "button",
      { class: "icon", type: "button", title: t().undoPoint, ...(result.closed ? { disabled: true } : {}) },
      "↶",
    );
    const finish = el(
      "button",
      { class: "icon", type: "button", title: t().finish, ...(result.closed || !finishNeeded ? { disabled: true } : {}) },
      "✓",
    );
    const edit = el(
      "button",
      { class: "icon", type: "button", title: t().editPoints, ...(result.closed ? {} : { disabled: true }) },
      "✎",
    );
    const save = el(
      "button",
      { class: "icon", type: "button", title: t().saveMeasurement, ...(result.closed ? {} : { disabled: true }) },
      "💾",
    );
    const clearButton = el("button", { class: "icon", type: "button", title: t().clearShape }, "✕");
    undo.addEventListener("click", () => measure.undo());
    finish.addEventListener("click", () => measure.finish());
    edit.addEventListener("click", () => measure.reopen());
    save.addEventListener("click", () => {
      const name = window.prompt(t().namePrompt, defaultName(result));
      if (name) store.saveCurrent(name);
    });
    clearButton.addEventListener("click", () => {
      measure.clear();
      store.startNew();
    });
    panel.append(el("div", { class: "row" }, undo, finish, edit, save, clearButton));

    panel.append(
      el("div", {
        class: "hint",
        text: result.closed ? t().hintClosed : result.mode === "line" ? t().hintLine : t().hintArea,
      }),
    );
  };

  measure.onChange = render;
  render(null);
  renderSaved();
  return { refreshSaved: renderSaved };
}

function defaultName(result: MeasureResult): string {
  const label = result.mode === "line" ? t().defaultLineName : result.mode === "area" ? t().defaultAreaName : t().defaultVolumeName;
  return `${label} ${new Date().toLocaleString()}`;
}
